/**
 * Trang tai khoan: dinh danh, han muc tu dat, choi co trach nhiem, phien dang nhap.
 *
 * Bon diem nghiep vu man hinh nay chiu trach nhiem:
 *
 *   1. HA han muc co hieu luc NGAY; NANG han muc phai qua thoi gian cho
 *      (RSK-R11). Day la khac biet quan trong nhat cua ca man hinh: no ton tai
 *      de mot quyet dinh boc dong khong go duoc rang buoc da tu dat.
 *   2. Cac muc thoi gian tam ngung do GOI PHAP LY quy dinh (NT-07), doc tu may
 *      chu chu khong viet cung trong giao dien.
 *   3. TU LOAI TRU khong go duoc truoc han, ke ca khi nguoi choi doi y. Cau nay
 *      phai nam truoc nut, khong phai sau khi da bam.
 *   4. Ten tren giay to dinh danh se duoc doi chieu voi ten chu tai khoan nhan
 *      luc rut tien (WDR-R32) — noi truoc de khong phai lam lai ho so.
 */

import { useEffect, useRef, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  type LimitDimension,
  type LimitPeriod,
  type LimitRow,
  type PlayerSession,
} from '../lib/api';
import { formatMoney, parseAmount } from '../lib/money';
import { useSession } from '../lib/session';
import { Badge, Button, Card, EmptyState, ErrorList, Field, Skeleton } from '../components/ui';
import styles from './Account.module.css';

export type AccountSection = 'tong-quan' | 'dinh-danh' | 'han-muc' | 'hoat-dong';

const DIMENSION_LABEL: Readonly<Record<string, string>> = {
  DEPOSIT: 'Nạp tiền',
  STAKE: 'Tiền cược',
  NET_LOSS: 'Lỗ ròng',
  WITHDRAWAL: 'Rút tiền',
};

const PERIOD_LABEL: Readonly<Record<string, string>> = {
  DAY: 'Mỗi ngày',
  WEEK: 'Mỗi tuần',
  MONTH: 'Mỗi tháng',
  SINGLE: 'Mỗi lần',
};

/** Nguoi choi tu dat duoc ba nhom nay; han muc rut do goi phap ly giu. */
const SELF_DIMENSIONS: LimitDimension[] = ['DEPOSIT', 'STAKE', 'NET_LOSS'];

const DOCUMENT_TYPES = [
  { value: 'PASSPORT', label: 'Hộ chiếu' },
  { value: 'NATIONAL_ID', label: 'Thẻ căn cước' },
  { value: 'DRIVING_LICENCE', label: 'Giấy phép lái xe' },
];

export function AccountPage({ section = 'tong-quan' }: { section?: AccountSection }): JSX.Element {
  const { isAuthenticated, me, logout } = useSession();

  const kycRef = useRef<HTMLDivElement>(null);
  const limitRef = useRef<HTMLDivElement>(null);
  const activityRef = useRef<HTMLDivElement>(null);

  // Cac lien ket tu man hinh nap/rut tro thang toi dung muc can xu ly.
  useEffect(() => {
    const target =
      section === 'dinh-danh' ? kycRef : section === 'han-muc' ? limitRef : section === 'hoat-dong' ? activityRef : null;
    target?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [section]);

  if (!isAuthenticated || !me) return <EmptyState title="Đăng nhập để xem tài khoản" />;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Tài khoản</h1>

      <Card title="Thông tin chung">
        <div className={styles.info}>
          <InfoRow label="Tên đăng nhập" value={me.username} />
          <InfoRow label="Tiền tệ" value={me.currency} />
          <InfoRow label="Quốc gia" value={me.country_code} />
          {/* Ten bac dinh danh do goi phap ly dat — hien nguyen van, khong dich */}
          <InfoRow label="Bậc định danh" value={me.kyc_tier} />
        </div>

        {/* Tren dien thoai thanh tren cung khong co cho cho nut Thoat, nen dat o day. */}
        <div className={styles.logoutRow}>
          <Button size="sm" variant="secondary" onClick={() => void logout()}>
            Đăng xuất
          </Button>
        </div>

        {me.restrictions.length > 0 && (
          <div className={styles.restrictions}>
            <span className={styles.restrictionLabel}>Hạn chế đang áp dụng</span>
            <div className={styles.restrictionList}>
              {me.restrictions.map((r) => (
                <Badge key={r} tone="warning">{r}</Badge>
              ))}
            </div>
          </div>
        )}
      </Card>

      <div ref={kycRef}>
        <KycSection currentTier={me.kyc_tier} />
      </div>

      <div ref={limitRef}>
        <LimitsSection />
      </div>

      <ResponsibleGamingSection />

      <div ref={activityRef}>
        <SessionsSection />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function KycSection({ currentTier }: { currentTier: string }): JSX.Element {
  const { refresh } = useSession();
  const [fullName, setFullName] = useState('');
  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES[0]!.value);
  const [documentNumber, setDocumentNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);

  // Bac dinh danh doc tu goi phap ly, khong viet cung ten bac trong giao dien.
  const requirements = useQuery({
    queryKey: ['kyc-requirements'],
    queryFn: api.account.kycRequirements,
  });

  const requiredTier = requirements.data?.tier_required_for_withdrawal ?? null;
  const currentRank = requirements.data?.tiers.find((t) => t.name === currentTier)?.rank ?? -1;
  const requiredRank = requirements.data?.tiers.find((t) => t.name === requiredTier)?.rank ?? 99;
  const alreadyEnough = currentRank >= requiredRank;

  const submit = useMutation({
    mutationFn: () =>
      api.account.submitKyc({
        target_tier: requiredTier!,
        document_type: documentType,
        document_number: documentNumber,
        full_name: fullName,
        ...(expiry ? { document_expiry: expiry } : {}),
      }),
    onSuccess: (result) => {
      setSubmitted(result.status);
      refresh();
    },
  });

  const error = submit.error instanceof ApiError ? submit.error : null;

  return (
    <Card title="Định danh">
      {requirements.isLoading ? (
        <Skeleton height={120} />
      ) : submitted ? (
        <p className={styles.note}>
          Hồ sơ đã nộp và đang ở trạng thái <strong>{submitted}</strong>. Kết quả thẩm định sẽ hiện
          ở mục này, chậm nhất sau {requirements.data?.review_sla_hours ?? 24} giờ.
        </p>
      ) : alreadyEnough ? (
        <p className={styles.ok}>
          Bậc định danh hiện tại đã đủ điều kiện rút tiền.
        </p>
      ) : (
        <div className={styles.form}>
          <p className={styles.note}>
            Định danh là điều kiện bắt buộc trước lần rút tiền đầu tiên
            {requiredTier ? ` (cần đạt bậc ${requiredTier})` : ''}. Tên khai ở đây sẽ được đối
            chiếu với tên chủ tài khoản nhận lúc rút — khai lệch thì lệnh rút bị chặn.
          </p>

          <Field
            label="Họ và tên trên giấy tờ"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />

          <label className={styles.selectField}>
            <span className={styles.selectLabel}>Loại giấy tờ</span>
            <select
              className={styles.select}
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
            >
              {DOCUMENT_TYPES.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </label>

          <Field
            label="Số giấy tờ"
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            required
          />
          <Field
            label="Ngày hết hạn"
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            hint="Không bắt buộc"
          />

          {error && <ErrorList messages={error.messages} />}

          <Button
            loading={submit.isPending}
            disabled={fullName.length < 2 || documentNumber.length < 4 || requiredTier === null}
            onClick={() => submit.mutate()}
          >
            Nộp hồ sơ
          </Button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function LimitsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const limits = useQuery({ queryKey: ['limits'], queryFn: api.limits });

  if (limits.isLoading) return <Card title="Hạn mức tự đặt"><Skeleton height={120} /></Card>;
  if (!limits.data) return <Card title="Hạn mức tự đặt"><p className={styles.muted}>Không đọc được hạn mức.</p></Card>;

  return (
    <Card title="Hạn mức tự đặt">
      {/**
       * Quy tac quyet dinh ca muc nay, noi ngay dau chu khong de o cuoi:
       * ha thi co hieu luc ngay, nang thi phai cho.
       */}
      <p className={styles.note}>
        Hạ hạn mức có hiệu lực <strong>ngay lập tức</strong>. Nâng hạn mức phải qua thời gian chờ
        theo quy định — mốc hiệu lực hiển thị sau khi lưu.
      </p>

      <div className={styles.limitGroups}>
        {SELF_DIMENSIONS.map((dimension) => (
          <LimitGroup
            key={dimension}
            dimension={dimension}
            rows={limits.data.limits[dimension] ?? []}
            currency={limits.data.currency}
            onSaved={() => void queryClient.invalidateQueries({ queryKey: ['limits'] })}
          />
        ))}
      </div>
    </Card>
  );
}

function LimitGroup({
  dimension,
  rows,
  currency,
  onSaved,
}: {
  dimension: LimitDimension;
  rows: LimitRow[];
  currency: string;
  onSaved: () => void;
}): JSX.Element {
  const [period, setPeriod] = useState<LimitPeriod>('DAY');
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(null);

  const row = rows.find((r) => r.period === period);

  const save = useMutation({
    mutationFn: () =>
      api.account.setLimit({
        dimension,
        period,
        amount_minor: amount ? parseAmount(amount, currency).toString() : null,
      }),
    onSuccess: (result) => {
      setEffectiveFrom(result.effective_from);
      setAmount('');
      onSaved();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <div className={styles.limitGroup}>
      <div className={styles.limitHead}>
        <span className={styles.limitName}>{DIMENSION_LABEL[dimension] ?? dimension}</span>
        <div className={styles.periods}>
          {(['DAY', 'WEEK', 'MONTH'] as const).map((p) => (
            <button
              key={p}
              className={[styles.period, p === period ? styles.periodActive : ''].filter(Boolean).join(' ')}
              onClick={() => setPeriod(p)}
            >
              {PERIOD_LABEL[p]}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.limitState}>
        {row?.cap_minor ? (
          <>
            <span className={`numeric ${styles.limitCap}`}>
              {formatMoney({ minor: BigInt(row.cap_minor), currency })} {currency}
            </span>
            <span className={styles.limitUsed}>
              đã dùng{' '}
              <span className="numeric">
                {formatMoney({ minor: BigInt(row.used_minor), currency })}
              </span>
              {/* Nguon han muc: do nguoi choi tu dat, hay do goi phap ly ap */}
              {row.scope === 'SELF' ? ' · tự đặt' : ' · theo quy định'}
            </span>
          </>
        ) : (
          <span className={styles.muted}>Chưa đặt hạn mức</span>
        )}
      </div>

      <div className={styles.limitForm}>
        <input
          className={`numeric ${styles.limitInput}`}
          inputMode="decimal"
          placeholder="Hạn mức mới"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          aria-label={`Hạn mức ${DIMENSION_LABEL[dimension]} ${PERIOD_LABEL[period]}`}
        />
        <Button size="sm" loading={save.isPending} disabled={!amount} onClick={() => save.mutate()}>
          Lưu
        </Button>
        {row?.cap_minor && row.scope === 'SELF' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setAmount('');
              save.mutate();
            }}
          >
            Bỏ hạn mức
          </Button>
        )}
      </div>

      {error && <ErrorList messages={error.messages} />}

      {effectiveFrom && (
        <p className={styles.note}>
          Có hiệu lực từ{' '}
          <span className="numeric">{new Date(effectiveFrom).toLocaleString('vi-VN')}</span>.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ResponsibleGamingSection(): JSX.Element {
  const { refresh } = useSession();
  const [confirmExclusion, setConfirmExclusion] = useState(false);
  /**
   * `result` la moc ket thuc, va no LA NULL trong truong hop vinh vien — nen
   * khong dung duoc no de biet da ap dung hay chua. Can mot co rieng.
   */
  const [applied, setApplied] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // Cac muc thoi gian do goi phap ly quy dinh, khong viet cung trong giao dien.
  const options = useQuery({ queryKey: ['rg-options'], queryFn: api.account.rgOptions });

  const cooling = useMutation({
    mutationFn: (days: number) => api.account.coolingOff(days),
    onSuccess: (r) => {
      setResult(r.ends_at);
      setApplied(true);
      refresh();
    },
  });

  const exclude = useMutation({
    mutationFn: (days: number) => api.account.selfExclusion(days),
    onSuccess: (r) => {
      setResult(r.ends_at);
      setApplied(true);
      refresh();
    },
  });

  const error =
    cooling.error instanceof ApiError
      ? cooling.error
      : exclude.error instanceof ApiError
        ? exclude.error
        : null;

  return (
    <Card title="Chơi có trách nhiệm">
      {options.isLoading ? (
        <Skeleton height={100} />
      ) : options.data ? (
        <div className={styles.rg}>
          <div className={styles.rgBlock}>
            <span className={styles.rgTitle}>Tạm ngừng ngắn hạn</span>
            <p className={styles.note}>
              Tài khoản bị khóa đặt cược trong thời gian chọn, sau đó tự mở lại. Số dư vẫn rút được.
            </p>
            <div className={styles.rgOptions}>
              {options.data.cooling_off_days.map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant="secondary"
                  loading={cooling.isPending}
                  onClick={() => cooling.mutate(d)}
                >
                  {d} ngày
                </Button>
              ))}
            </div>
          </div>

          <div className={styles.rgBlock}>
            <span className={styles.rgTitle}>Tự loại trừ</span>
            {/* Cau canh bao dat TRUOC nut, va nut chi mo khi da danh dau xac nhan */}
            <p className={styles.rgWarn}>
              Tự loại trừ <strong>không gỡ được trước hạn</strong>, kể cả khi đổi ý. Hết hạn cũng
              không tự mở lại: phải yêu cầu kích hoạt lại và chờ thêm thời gian nguội.
              {options.data.self_exclusion_days.some((d) => d < 0) && (
                <>
                  {' '}
                  Mức <strong>vĩnh viễn</strong> thì không có ngày kết thúc và không gỡ được trong
                  bất kỳ trường hợp nào.
                </>
              )}
            </p>
            <label className={styles.rgConsent}>
              <input
                type="checkbox"
                checked={confirmExclusion}
                onChange={(e) => setConfirmExclusion(e.target.checked)}
              />
              <span>Đã đọc và hiểu điều trên.</span>
            </label>
            <div className={styles.rgOptions}>
              {options.data.self_exclusion_days.map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant="danger"
                  disabled={!confirmExclusion}
                  loading={exclude.isPending}
                  onClick={() => exclude.mutate(d)}
                >
                  {exclusionLabel(d)}
                </Button>
              ))}
            </div>
          </div>

          <p className={styles.note}>
            Nhắc phiên chơi hiện ra sau mỗi {options.data.session_reminder_minutes} phút.
          </p>

          {error && <ErrorList messages={error.messages} />}

          {applied && (
            <p className={styles.ok}>
              {result
                ? `Đã áp dụng, kết thúc lúc ${new Date(result).toLocaleString('vi-VN')}.`
                : 'Đã áp dụng vĩnh viễn, không có ngày kết thúc.'}
            </p>
          )}
        </div>
      ) : (
        <p className={styles.muted}>Không đọc được cấu hình.</p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function SessionsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: api.account.sessions });

  const revoke = useMutation({
    mutationFn: (id: string) => api.account.revokeSession(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  return (
    <Card title="Phiên đang đăng nhập">
      {sessions.isLoading ? (
        <Skeleton height={60} />
      ) : sessions.data && sessions.data.sessions.length > 0 ? (
        <ul className={styles.sessions}>
          {sessions.data.sessions.map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              onRevoke={() => revoke.mutate(s.id)}
              busy={revoke.isPending}
            />
          ))}
        </ul>
      ) : (
        <p className={styles.muted}>Không có phiên nào đang mở.</p>
      )}
      <p className={styles.note}>
        Thu hồi một phiên sẽ đăng xuất thiết bị đó ngay. Thấy phiên lạ thì thu hồi và đổi mật khẩu.
      </p>
    </Card>
  );
}

function SessionRow({
  session,
  onRevoke,
  busy,
}: {
  session: PlayerSession;
  onRevoke: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <li className={styles.session}>
      <div className={styles.sessionMain}>
        <span className={styles.sessionDevice}>{session.ip ?? 'Không rõ địa chỉ'}</span>
        <span className={styles.sessionTime}>
          Hoạt động gần nhất{' '}
          <span className="numeric">{new Date(session.last_seen_at).toLocaleString('vi-VN')}</span>
        </span>
      </div>
      <Button size="sm" variant="ghost" loading={busy} onClick={onRevoke}>
        Thu hồi
      </Button>
    </li>
  );
}

/**
 * Nhan cho mot muc tu loai tru.
 *
 * Goi phap ly dung so am de chi VINH VIEN — ban ghi luc do khong co moc ket
 * thuc va may chu tu choi go trong moi truong hop. Hien nguyen so am ra man
 * hinh ("-1 ngay") thi nguoi choi khong the hieu minh dang chon cai gi.
 */
function exclusionLabel(days: number): string {
  if (days < 0) return 'Vĩnh viễn';
  if (days >= 365 && days % 365 === 0) return `${days / 365} năm`;
  return `${days} ngày`;
}

function InfoRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className={styles.infoRow}>
      <span className={styles.muted}>{label}</span>
      <span className={styles.infoValue}>{value}</span>
    </div>
  );
}
